import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  createPlaylist,
  getAllPlaylists,
  getPlaylistById,
  updatePlaylist,
  deletePlaylist,
  getUserPlaylists,
  getPlaylistsByType,
  getPlaylistStats,
  checkContentInPlaylist
} from '../api/playListAPI';

// Hook for creating a new playlist
export const useCreatePlaylist = () => {
  const queryClient = useQueryClient();
  
  return useMutation(createPlaylist, {
    onSuccess: () => {
      toast.success('Playlist created successfully!');
      queryClient.invalidateQueries('playlists');
      queryClient.invalidateQueries('userPlaylists');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create playlist';
      toast.error(errorMessage);
    }
  });
};

// Hook for getting all playlists
export const useAllPlaylists = (page = 1, limit = 10) => {
  return useQuery(
    ['playlists', page, limit],
    () => getAllPlaylists(page, limit),
    {
      keepPreviousData: true,
      staleTime: 5000
    }
  );
};

// Hook for getting a playlist by ID
export const usePlaylist = (id) => {
  return useQuery(
    ['playlist', id],
    () => getPlaylistById(id),
    {
      enabled: !!id,
      staleTime: 5000
    }
  );
};

// Hook for updating a playlist
export const useUpdatePlaylist = () => {
  const queryClient = useQueryClient();
  
  return useMutation(updatePlaylist, {
    onSuccess: (data) => {
      toast.success('Playlist updated successfully!');
      queryClient.invalidateQueries(['playlist', data.playlist.id]);
      queryClient.invalidateQueries('playlists');
      queryClient.invalidateQueries('userPlaylists');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update playlist';
      toast.error(errorMessage);
    }
  });
};

// Hook for deleting a playlist
export const useDeletePlaylist = () => {
  const queryClient = useQueryClient();
  
  return useMutation(deletePlaylist, {
    onSuccess: () => {
      toast.success('Playlist deleted successfully!');
      queryClient.invalidateQueries('playlists');
      queryClient.invalidateQueries('userPlaylists');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete playlist';
      toast.error(errorMessage);
    }
  });
};

// Hook for getting current user's playlists
export const useUserPlaylists = (page = 1, limit = 10) => {
  return useQuery(
    ['userPlaylists', page, limit],
    () => getUserPlaylists(page, limit),
    {
      keepPreviousData: true,
      staleTime: 5000
    }
  );
};

// Hook for getting playlists by content type
export const usePlaylistsByType = (type, page = 1, limit = 10) => {
  return useQuery(
    ['playlists', 'type', type, page, limit],
    () => getPlaylistsByType(type, page, limit),
    {
      enabled: !!type,
      keepPreviousData: true,
      staleTime: 5000
    }
  );
};

// Hook for getting playlist statistics
export const usePlaylistStats = () => {
  return useQuery(
    'playlistStats',
    getPlaylistStats,
    {
      staleTime: 60000 // 1 minute
    }
  );
};

// Hook for checking if content is in user's playlist
export const useCheckContentInPlaylist = (item_type, content_id) => {
  return useQuery(
    ['playlistCheck', item_type, content_id],
    () => checkContentInPlaylist(item_type, content_id),
    {
      enabled: !!item_type && !!content_id,
      staleTime: 5000
    }
  );
};